import React, { createContext, useState, useEffect, useContext } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { getMyLinkedUsers, getLinkedToMe, checkAdminStatus, getTenantInfo } from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [linkedUsers, setLinkedUsers] = useState([]);
  const [linkedToMe, setLinkedToMe] = useState([]);
  const [tenantInfo, setTenantInfo] = useState(null);

  useEffect(() => {
    // テナント情報を最初に取得（認証不要）
    const loadTenantInfo = async () => {
      try {
        const info = await getTenantInfo();
        setTenantInfo(info);
      } catch (error) {
        console.error('テナント情報の取得に失敗:', error);
      }
    };
    
    loadTenantInfo();

    const unsubscribe = onAuthStateChanged(auth, async user => {
      setCurrentUser(user);
      if (user) {
        setIsEmailVerified(user.emailVerified);
        try {
          const [users, toMe] = await Promise.all([
            getMyLinkedUsers(),
            getLinkedToMe()
          ]);
          setLinkedUsers(users);
          setLinkedToMe(toMe);
        } catch (e) {
          setLinkedUsers([]);
          setLinkedToMe([]);
        }
        try {
          const { is_tenant_admin } = await checkAdminStatus();
          setIsAdmin(is_tenant_admin);
        } catch (error) {
          console.error('管理者権限の確認に失敗:', error);
          setIsAdmin(false);
        }
      } else {
        setIsEmailVerified(false);
        setLinkedUsers([]);
        setLinkedToMe([]);
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // 機能が有効かどうかをチェックする関数
  const isFunctionEnabled = (functionName) => {
    if (!tenantInfo?.enable_functions) return true; // デフォルトで有効
    return tenantInfo.enable_functions[functionName] !== false;
  };

  const value = {
    currentUser,
    isEmailVerified,
    loading,
    linkedUsers,
    linkedToMe,
    isAdmin,
    tenantInfo,
    isFunctionEnabled
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 